/**
 * AuditPanel — журнал всех действий через DevOps Console (P3 wave-1).
 *
 * Источник данных: GET /api/v1/devops/audit (+ /audit/stats для шапки).
 * Фильтры: user / path / method / только деструктивные.
 * Пагинация: limit + offset.
 */

import { useEffect, useMemo, useState } from "react";
import {
  RefreshCw,
  ShieldAlert,
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  CircleAlert,
} from "lucide-react";
import {
  fetchDevopsAudit,
  fetchDevopsAuditStats,
  type DevopsAuditEntry,
  type DevopsAuditStats,
} from "../../lib/api";
import { cn } from "../../lib/cn";

const PAGE_SIZE = 50;

function formatTs(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", { hour12: false });
  } catch {
    return iso;
  }
}

function statusBadge(code: number): { color: string; label: string } {
  if (code >= 200 && code < 300) return { color: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30", label: String(code) };
  if (code === 409) return { color: "text-amber-500 bg-amber-500/10 border-amber-500/30", label: "409" };
  if (code >= 400 && code < 500) return { color: "text-orange-500 bg-orange-500/10 border-orange-500/30", label: String(code) };
  if (code >= 500) return { color: "text-rose-500 bg-rose-500/10 border-rose-500/30", label: String(code) };
  return { color: "text-fg-subtle bg-bg-subtle border-border/50", label: String(code || "?") };
}

export function AuditPanel() {
  const [items, setItems] = useState<DevopsAuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [stats, setStats] = useState<DevopsAuditStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [user, setUser] = useState("");
  const [pathFilter, setPathFilter] = useState("");
  const [method, setMethod] = useState("");
  const [onlyDestructive, setOnlyDestructive] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    setErr(null);
    try {
      const data = await fetchDevopsAudit({
        limit: PAGE_SIZE,
        offset,
        user: user.trim() || undefined,
        path: pathFilter.trim() || undefined,
        method: method.trim() || undefined,
        onlyDestructive,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function reloadStats() {
    setStatsLoading(true);
    try {
      const s = await fetchDevopsAuditStats(24);
      setStats(s);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, onlyDestructive]);

  useEffect(() => {
    reloadStats();
  }, []);

  const pageInfo = useMemo(() => {
    const from = total === 0 ? 0 : offset + 1;
    const to = Math.min(offset + items.length, total);
    return `${from}–${to} из ${total}`;
  }, [items.length, offset, total]);

  function applyFilters() {
    setOffset(0);
    reload();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ShieldAlert className="size-5 text-accent-500" />
        <h2 className="text-h2">Audit log</h2>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              reload();
              reloadStats();
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-small bg-bg-subtle hover:bg-bg-card border border-border/40 transition-colors"
            disabled={loading}
          >
            <RefreshCw className={cn("size-3.5", loading && "animate-spin")} />
            Обновить
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Всего за 24ч"
          value={stats?.total ?? "—"}
          loading={statsLoading}
          tone="neutral"
        />
        <StatCard
          label="С подтверждением"
          value={stats?.confirms ?? "—"}
          loading={statsLoading}
          tone="amber"
        />
        <StatCard
          label="Ошибок (4xx/5xx)"
          value={stats?.errors ?? "—"}
          loading={statsLoading}
          tone="rose"
        />
        <StatCard
          label="Топ-пути"
          value={stats?.top_paths?.[0]?.path ? stats.top_paths[0].path.replace("/api/v1/devops/", "") : "—"}
          loading={statsLoading}
          tone="neutral"
          small
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2 p-3 rounded-lg bg-bg-card/40 border border-border/40">
        <div className="flex-1 min-w-[140px]">
          <label className="text-[11px] uppercase font-mono text-fg-subtle">Пользователь</label>
          <input
            type="text"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            placeholder="email или uid"
            className="w-full mt-1 px-2 py-1.5 rounded-md bg-bg-subtle border border-border/40 text-small focus:outline-none focus:ring-1 focus:ring-accent-500"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-[11px] uppercase font-mono text-fg-subtle">Путь содержит</label>
          <input
            type="text"
            value={pathFilter}
            onChange={(e) => setPathFilter(e.target.value)}
            placeholder="/processes, /service/, /files/write..."
            className="w-full mt-1 px-2 py-1.5 rounded-md bg-bg-subtle border border-border/40 text-small focus:outline-none focus:ring-1 focus:ring-accent-500"
          />
        </div>
        <div className="w-32">
          <label className="text-[11px] uppercase font-mono text-fg-subtle">Method</label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 rounded-md bg-bg-subtle border border-border/40 text-small focus:outline-none focus:ring-1 focus:ring-accent-500"
          >
            <option value="">все</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>
        <label className="inline-flex items-center gap-2 text-small cursor-pointer h-9 px-3 rounded-md bg-bg-subtle border border-border/40 hover:bg-bg-card">
          <input
            type="checkbox"
            checked={onlyDestructive}
            onChange={(e) => {
              setOnlyDestructive(e.target.checked);
              setOffset(0);
            }}
            className="accent-accent-500"
          />
          <AlertTriangle className="size-3.5 text-amber-500" />
          Только деструктивные
        </label>
        <button
          type="button"
          onClick={applyFilters}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-small bg-accent-500/15 text-accent-500 border border-accent-500/30 hover:bg-accent-500/20"
        >
          <Filter className="size-3.5" />
          Применить
        </button>
      </div>

      {err && (
        <div className="p-3 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-500 text-small">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border border-border/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-small">
            <thead className="bg-bg-card/60 text-fg-subtle text-[11px] uppercase font-mono">
              <tr>
                <th className="text-left px-3 py-2 font-normal">Время</th>
                <th className="text-left px-3 py-2 font-normal">Пользователь</th>
                <th className="text-left px-3 py-2 font-normal">Method</th>
                <th className="text-left px-3 py-2 font-normal">Path</th>
                <th className="text-left px-3 py-2 font-normal">Status</th>
                <th className="text-left px-3 py-2 font-normal">ms</th>
                <th className="text-left px-3 py-2 font-normal w-12">⚠</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-fg-subtle">
                    Записей нет
                  </td>
                </tr>
              )}
              {items.map((it) => {
                const sb = statusBadge(it.status_code);
                const isExpanded = expanded === it.id;
                return (
                  <>
                    <tr
                      key={it.id}
                      className={cn(
                        "border-t border-border/30 cursor-pointer transition-colors",
                        isExpanded ? "bg-bg-card/40" : "hover:bg-bg-subtle/30",
                      )}
                      onClick={() => setExpanded(isExpanded ? null : it.id)}
                    >
                      <td className="px-3 py-2 font-mono text-[11px] text-fg-muted whitespace-nowrap">
                        {formatTs(it.ts)}
                      </td>
                      <td className="px-3 py-2 truncate max-w-[180px]">
                        {it.user_email || it.user_id || (
                          <span className="text-fg-subtle italic">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px]">{it.method}</td>
                      <td className="px-3 py-2 font-mono text-[11px] truncate max-w-[280px]">
                        {it.path.replace("/api/v1/devops/", "/")}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={cn(
                            "inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-mono",
                            sb.color,
                          )}
                        >
                          {sb.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-fg-muted text-[11px]">
                        {it.duration_ms}
                      </td>
                      <td className="px-3 py-2">
                        {it.confirm && (
                          <span title="С confirm=true">
                            <AlertTriangle className="size-3.5 text-amber-500" />
                          </span>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={it.id + "-d"} className="bg-bg-card/30 border-t border-border/20">
                        <td colSpan={7} className="px-3 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
                            <KV k="ID" v={it.id} mono />
                            <KV k="IP" v={it.ip || "—"} mono />
                            <KV k="User-Agent" v={it.user_agent || "—"} mono />
                            <KV k="Status" v={String(it.status_code)} mono />
                            {it.error && <KV k="Error" v={it.error} mono className="md:col-span-2 text-rose-500" />}
                            <div className="md:col-span-2">
                              <div className="text-[10px] uppercase font-mono text-fg-subtle mb-1">Args summary</div>
                              <pre className="font-mono text-[11px] bg-bg-subtle/60 border border-border/30 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap break-all">
                                {it.args_summary || "—"}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-small text-fg-muted">
        <div>{pageInfo}</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={offset <= 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-bg-subtle border border-border/40 hover:bg-bg-card disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="size-3.5" /> Назад
          </button>
          <button
            type="button"
            disabled={offset + items.length >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-bg-subtle border border-border/40 hover:bg-bg-card disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Вперёд <ChevronRight className="size-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  loading,
  tone,
  small,
}: {
  label: string;
  value: string | number;
  loading: boolean;
  tone: "neutral" | "amber" | "rose";
  small?: boolean;
}) {
  const toneCls =
    tone === "amber"
      ? "border-amber-500/30 bg-amber-500/5"
      : tone === "rose"
        ? "border-rose-500/30 bg-rose-500/5"
        : "border-border/40 bg-bg-card/40";
  const Icon = tone === "amber" ? AlertTriangle : tone === "rose" ? CircleAlert : CheckCircle2;
  return (
    <div className={cn("rounded-lg border p-3", toneCls)}>
      <div className="flex items-center gap-2 text-[11px] uppercase font-mono text-fg-subtle">
        <Icon className="size-3.5" />
        {label}
      </div>
      <div className={cn("mt-1 font-semibold", small ? "text-small font-mono truncate" : "text-h2")}>
        {loading ? "…" : value}
      </div>
    </div>
  );
}

function KV({
  k,
  v,
  mono,
  className,
}: {
  k: string;
  v: string;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("flex gap-2 items-baseline", className)}>
      <span className="text-[10px] uppercase font-mono text-fg-subtle shrink-0 w-24">{k}</span>
      <span className={cn("break-all", mono && "font-mono text-[11px]")}>{v}</span>
    </div>
  );
}
